import { Injectable, inject } from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ToastComponent } from './toast.component';

type ToastTone = 'success' | 'error' | 'info';

/**
 * Drop-in replacement for MatSnackBar.
 * Uses CDK overlay for placement and CDK LiveAnnouncer for a11y.
 * Styling lives in the design-system (.toast) — no Material dependency.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly overlay = inject(Overlay);
  private readonly announcer = inject(LiveAnnouncer);

  success(message: string, durationMs = 3000): void {
    this.show(message, 'success', durationMs);
  }

  error(message: string, durationMs = 4500): void {
    this.show(message, 'error', durationMs);
  }

  info(message: string, durationMs = 3000): void {
    this.show(message, 'info', durationMs);
  }

  private show(message: string, tone: ToastTone, durationMs: number): void {
    const overlayRef = this.overlay.create({
      positionStrategy: this.overlay.position().global().centerHorizontally().bottom('72px'),
      hasBackdrop: false,
      disposeOnNavigation: true,
    });

    const ref = overlayRef.attach(new ComponentPortal(ToastComponent));
    ref.setInput('message', message);
    ref.setInput('tone', tone);

    this.announcer.announce(message, tone === 'error' ? 'assertive' : 'polite');

    setTimeout(() => overlayRef.dispose(), durationMs);
  }
}
